import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { setCurrentOrgId, getCurrentOrgId, clearCurrentOrgId } from '../authClient';
import { useAuthState } from './ClerkProviderWrapper';

/**
 * Organization context for multi-tenant support.
 * This context manages the current organization and provides
 * organization-related functionality.
 */

const OrganizationContext = createContext(null);

export function useOrganization() {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
}

export function OrganizationProvider({ children }) {
    const [organizations, setOrganizations] = useState([]);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isLoaded, isSignedIn } = useAuthState();

    // Fetch user's organizations once Clerk is loaded and user is signed in
    useEffect(() => {
        if (!isLoaded || !isSignedIn) {
            if (isLoaded && !isSignedIn) setIsLoading(false);
            return;
        }

        async function fetchOrganizations() {
            try {
                setIsLoading(true);
                const response = await api.get('/api/users/me/organizations');
                setOrganizations(response.data);

                // Auto-select first organization if none selected
                const storedOrgId = localStorage.getItem('driftops_current_org_id');
                if (storedOrgId) {
                    const org = response.data.find(o => o.id === storedOrgId);
                    if (org) {
                        setCurrentOrg(org);
                        setCurrentOrgId(org.id);
                        return;
                    }
                }

                // Select first organization by default
                if (response.data.length > 0) {
                    const firstOrg = response.data[0];
                    setCurrentOrg(firstOrg);
                    setCurrentOrgId(firstOrg.id);
                    localStorage.setItem('driftops_current_org_id', firstOrg.id);
                }
            } catch (err) {
                console.error('Failed to fetch organizations:', err);
                setError(err.response?.data?.detail || 'Failed to load organizations');
            } finally {
                setIsLoading(false);
            }
        }

        fetchOrganizations();
    }, [isLoaded, isSignedIn]);

    // Switch organization
    const switchOrganization = useCallback((orgId) => {
        const org = organizations.find(o => o.id === orgId);
        if (org) {
            setCurrentOrg(org);
            setCurrentOrgId(org.id);
            localStorage.setItem('driftops_current_org_id', org.id);
        }
    }, [organizations]);

    // Clear organization on logout
    const clearOrganization = useCallback(() => {
        setCurrentOrg(null);
        clearCurrentOrgId();
        localStorage.removeItem('driftops_current_org_id');
    }, []);

    // Get user's role in current org
    const getUserRole = useCallback(() => {
        if (!currentOrg) return null;
        // The organization data should include the user's role
        return currentOrg.role || 'viewer';
    }, [currentOrg]);

    // Check if user has specific permission
    const hasPermission = useCallback((permission) => {
        const role = getUserRole();
        if (!role) return false;

        const rolePermissions = {
            'admin': ['create', 'read', 'update', 'delete', 'manage'],
            'editor': ['create', 'read', 'update'],
            'viewer': ['read'],
        };

        const permissions = rolePermissions[role] || [];
        return permissions.includes(permission);
    }, [getUserRole]);

    // Calculate permissions based on current role - stable reference
    const userRole = currentOrg?.role || null;
    const userPermissions = useMemo(() => {
        if (!userRole) return [];
        return {
            'admin': ['create', 'read', 'update', 'delete', 'manage'],
            'editor': ['create', 'read', 'update'],
            'viewer': ['read'],
        }[userRole] || [];
    }, [userRole]);

    const checkPermission = useCallback((permission) => {
        return userPermissions.includes(permission);
    }, [userPermissions]);

    const value = useMemo(() => ({
        organizations,
        currentOrg,
        isLoading,
        error,
        switchOrganization,
        clearOrganization,
        getUserRole,
        hasPermission,
        isAdmin: userPermissions.includes('manage'),
        isEditor: userPermissions.includes('update'),
        canRead: userPermissions.includes('read'),
        canCreate: userPermissions.includes('create'),
        canDelete: userPermissions.includes('delete'),
    }), [organizations, currentOrg, isLoading, error, switchOrganization, clearOrganization, getUserRole, hasPermission, userPermissions]);

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
}

// Re-export for convenience
export { OrganizationContext };
